import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AddressType } from '@common';
import { TranslateService } from '@i18n';
import { FormControlValidators } from '../../../shared/utils/form-control-validators/form-control-validators.utils';
import { FormAdditionalAddress } from '../../models/form-additional-address/form-additional-address.interface';
import { FormAddress } from '../../models/form-address/form-address.interface';
import { FormManualAddress } from '../../models/form-manual-address/form-manual-address.interface';
import { FormOverrideAddress } from '../../models/form-override-address/form-override-address.interface';
import { AddressTranslationDictionary } from '../../models/i18n/address-translation-dictionary.interface';

@Injectable()
export class AddressFormBuilder {
  protected readonly formBuilder = inject(UntypedFormBuilder);
  protected readonly translateService =
    inject<TranslateService<AddressTranslationDictionary>>(TranslateService);

  buildAdditionalAddressForm(
    address?: Partial<FormAdditionalAddress>,
  ): UntypedFormGroup {
    return this.formBuilder.group(
      this.buildAdditionalAddressFormConfig(address),
    );
  }

  buildAddressOverrideForm(
    address?: Partial<FormOverrideAddress>,
  ): UntypedFormGroup {
    return this.formBuilder.group(
      this.buildAddressOverrideFormConfig(address),
      {
        validators: Validators.compose([
          this.requiredTrueWhenAddressIsCommercial(
            this.translateService.translate(
              'ADDRESS_IS_OVERRIDE_ADDRESS_ERROR_REQUIRED',
            ),
            'isOverrideAddress',
          ),
        ]),
      },
    );
  }

  buildAddressDropdownOverrideForm(
    address?: Partial<FormOverrideAddress>,
  ): UntypedFormGroup {
    return this.formBuilder.group(
      {
        ...this.buildManualAddressFormConfig(address),
        id: new UntypedFormControl(
          null,
          Validators.compose([
            FormControlValidators.required(
              this.translateService.translate('ADDRESS_ID_ERROR_REQUIRED'),
            ),
          ]),
        ),
        type: new UntypedFormControl({ value: address?.type }),
        isOverrideAddress: new UntypedFormControl(address?.isOverrideAddress),
      },
      {
        validators: Validators.compose([
          this.requiredTrueWhenAddressIsCommercial(
            this.translateService.translate(
              'ADDRESS_IS_OVERRIDE_ADDRESS_ERROR_REQUIRED',
            ),
            'isOverrideAddress',
          ),
        ]),
      },
    );
  }

  buildAddressForm(address?: Partial<FormAddress>): UntypedFormGroup {
    return this.formBuilder.group(this.buildAddressFormConfig(address));
  }

  buildManualAddressForm(
    address?: Partial<FormManualAddress>,
  ): UntypedFormGroup {
    return this.formBuilder.group(this.buildManualAddressFormConfig(address));
  }

  protected buildAdditionalAddressFormConfig(
    address?: Partial<FormAdditionalAddress>,
  ): Record<keyof FormAdditionalAddress, AbstractControl> {
    const isSameAsAddress = Boolean(address?.sameAsAddress ?? true);

    return {
      ...this.buildAddressFormConfig(address, isSameAsAddress),
      sameAsAddress: new UntypedFormControl({
        value: isSameAsAddress,
        disabled: !isSameAsAddress,
      }),
    };
  }

  protected buildAddressOverrideFormConfig(
    address?: Partial<FormOverrideAddress>,
  ): Record<keyof FormOverrideAddress, AbstractControl> {
    return {
      ...this.buildAddressFormConfig(address),
      isOverrideAddress: new UntypedFormControl(address?.isOverrideAddress),
    };
  }

  protected buildAddressFormConfig(
    address?: Partial<FormAddress>,
    disabled?: boolean,
  ): Record<keyof FormAddress, AbstractControl> {
    const isManualAddress: boolean = !address?.id && !!address?.postalCode;

    return {
      ...this.buildManualAddressFormConfig(address, disabled),
      id: new UntypedFormControl(
        { value: address?.id, disabled: isManualAddress || disabled },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate('ADDRESS_ID_ERROR_REQUIRED'),
          ),
        ]),
      ),
      type: new UntypedFormControl({ value: address?.type, disabled }),
    };
  }

  protected buildManualAddressFormConfig(
    address?: Partial<FormManualAddress>,
    disabled?: boolean,
  ): Record<keyof FormManualAddress, AbstractControl> {
    return {
      addressLine1: new UntypedFormControl(
        { value: address?.addressLine1, disabled },
        Validators.compose([]),
      ),
      addressLine2: new UntypedFormControl(
        { value: address?.addressLine2, disabled },
        Validators.compose([]),
      ),
      addressLine3: new UntypedFormControl(
        { value: address?.addressLine3, disabled },
        Validators.compose([]),
      ),
      addressLine4: new UntypedFormControl(
        { value: address?.addressLine4, disabled },
        Validators.compose([]),
      ),
      postalCode: new UntypedFormControl(
        { value: address?.postalCode, disabled },
        Validators.compose([]),
      ),
      countryCode: new UntypedFormControl(),
      countryIso2: new UntypedFormControl(),
      countryIso3: new UntypedFormControl(),
      countryIsoNumber: new UntypedFormControl(),
      countryName: new UntypedFormControl(),
    };
  }

  protected requiredTrueWhenAddressIsCommercial(
    errorMessage: string,
    key: string,
  ): ValidatorFn {
    const validator = FormControlValidators.requiredTrue(errorMessage);

    return (formGroup: AbstractControl) => {
      const control = formGroup.get(key);
      const addressTypeControl = formGroup.get('type');

      if (!addressTypeControl || !control) {
        return null;
      }

      if (
        addressTypeControl.value === AddressType.Commercial ||
        !addressTypeControl.value
      ) {
        control.addValidators(validator);
      } else {
        control.removeValidators(validator);
      }

      control.updateValueAndValidity({ onlySelf: true });

      return null;
    };
  }
}
